import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import {
  Shovel,
  Loader2,
  X as XIcon,
  Copy as CopyIcon,
  ChevronLeft,
  Users,
  Clock,
  Music,
  Check,
  Package,
} from 'lucide-react';
import { toast } from 'sonner';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import GenreAlbumTile from '@/components/GenreAlbumTile';
import api from '@/lib/api';

/**
 * DigSessionModal — the "live browse-each-other-crates" experience.
 *
 * Flow:
 *   1. Host taps "Start Dig" → we call /dig/create, get a 6-char code + QR
 *   2. Guest scans the QR OR types the code + taps Join
 *   3. Once both are in, both users see the peer's crate list
 *   4. Tap a crate → see tracks → tap a track → borrow (goes into own collection)
 *   5. Live activity toasts when your peer borrows something from you
 *   6. 5-minute timer, either side can close early
 *
 * Transport: HTTP polling every 3s. Simple, reliable, iOS-friendly.
 */
export default function DigSessionModal({
  open,
  onClose,
  initialMode = 'chooser',  // 'chooser' | 'host' | 'guest'
  initialCode = null,        // pre-fill for deep-link /dig/{code}
}) {
  const [step, setStep] = useState('chooser'); // chooser | host-waiting | guest-code | active | ended
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState(null);
  const [role, setRole] = useState(null);
  const [status, setStatus] = useState(null); // last poll response
  const [manualCode, setManualCode] = useState('');
  const [peerAlbums, setPeerAlbums] = useState([]);
  const [selectedBucket, setSelectedBucket] = useState(null);
  const [peerCards, setPeerCards] = useState([]);
  const [loadingPeerCards, setLoadingPeerCards] = useState(false);
  const [targetBucket, setTargetBucket] = useState('');
  const [myBuckets, setMyBuckets] = useState([]);
  const [borrowingId, setBorrowingId] = useState(null);
  const seenActivityRef = useRef(new Set());
  const pollRef = useRef(null);

  const reset = useCallback(() => {
    setStep('chooser');
    setCode(null);
    setRole(null);
    setStatus(null);
    setManualCode('');
    setPeerAlbums([]);
    setSelectedBucket(null);
    setPeerCards([]);
    setTargetBucket('');
    seenActivityRef.current = new Set();
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  // -------- Host flow --------
  const handleHost = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/dig/create');
      setCode(data.code);
      setRole('host');
      setStep('host-waiting');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start Dig session');
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      reset();
      return;
    }
    // Load caller's own buckets so the borrow flow has a target-crate picker.
    api
      .get('/cards/albums')
      .then(({ data }) => setMyBuckets((data?.albums || []).map((a) => a.genre_bucket)))
      .catch(() => setMyBuckets([]));
    // If a deep-link code was provided, jump straight into guest-join.
    if (initialCode) {
      setManualCode(initialCode);
      setStep('guest-code');
    } else if (initialMode === 'host') {
      handleHost();
    } else if (initialMode === 'guest') {
      setStep('guest-code');
    } else {
      setStep('chooser');
    }
  }, [open, initialMode, initialCode, reset, handleHost]);

  // -------- Guest flow --------
  const handleJoin = useCallback(async (rawCode) => {
    const c = (rawCode || manualCode || '').toUpperCase().trim();
    if (!c) {
      toast.error('Enter a Dig code');
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/dig/${encodeURIComponent(c)}/join`);
      setCode(data.code);
      setRole('guest');
      setStep('active');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not join session');
    } finally {
      setBusy(false);
    }
  }, [manualCode]);

  // -------- Polling once a code exists --------
  useEffect(() => {
    if (!code) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/dig/${encodeURIComponent(code)}`);
        if (cancelled) return;
        setStatus(data);
        // Once the guest has joined (from host's POV), advance to active.
        if (step === 'host-waiting' && data.status === 'active' && data.peer_present) {
          setStep('active');
        }
        // Auto-close on backend timeout / other-side-closed.
        if (data.status === 'closed') {
          setStep('ended');
          if (pollRef.current) clearInterval(pollRef.current);
          return;
        }
        // Emit toasts for new activity events (only for events involving OTHERS,
        // and only after the initial fetch — we prime the seen set on first poll).
        const acts = data.activity || [];
        const firstPoll = seenActivityRef.current.size === 0;
        for (const ev of acts) {
          const key = `${ev.kind}:${ev.at}:${ev.user_id}:${ev.card_id || ''}`;
          if (seenActivityRef.current.has(key)) continue;
          seenActivityRef.current.add(key);
          if (firstPoll) continue;
          if (ev.kind === 'joined' && ev.user_id !== undefined) {
            // If it's the peer joining, celebrate.
            const isPeer = (data.role === 'host' && ev.user_id !== data?.host_id_local);
            if (isPeer) toast.success(`@${ev.username} joined the Dig`);
          } else if (ev.kind === 'borrowed') {
            // Only notify if the OTHER user borrowed FROM us.
            const iBorrowed = ev.username === (data.role === 'host' ? data.host_username : data.guest_username);
            if (!iBorrowed) {
              toast(
                `@${ev.username} took "${ev.card_title || 'a track'}" from your crate`,
                { icon: '🤝' },
              );
            }
          } else if (ev.kind === 'closed') {
            toast.info(`Dig session ended by @${ev.username}`);
          }
        }
      } catch (e) {
        if (e?.response?.status === 410 || e?.response?.status === 404) {
          if (!cancelled) {
            setStep('ended');
            if (pollRef.current) clearInterval(pollRef.current);
          }
        }
      }
    };
    poll();
    pollRef.current = setInterval(poll, 3000);
    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [code, step]);

  // -------- Load peer's albums once active --------
  useEffect(() => {
    if (step !== 'active' || !code) return;
    let cancelled = false;
    api
      .get(`/dig/${encodeURIComponent(code)}/peer/albums`)
      .then(({ data }) => {
        if (!cancelled) setPeerAlbums(data?.albums || []);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [step, code]);

  // -------- Load peer cards on bucket select --------
  const openBucket = useCallback(async (bucket) => {
    setSelectedBucket(bucket);
    setLoadingPeerCards(true);
    try {
      const { data } = await api.get(`/dig/${encodeURIComponent(code)}/peer/cards`, {
        params: { bucket },
      });
      setPeerCards(data?.cards || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load tracks');
      setPeerCards([]);
    } finally {
      setLoadingPeerCards(false);
    }
  }, [code]);

  // -------- Borrow a track --------
  const borrowCard = useCallback(async (card) => {
    setBorrowingId(card.id);
    try {
      const body = { card_id: card.id, signed: true };
      if (targetBucket) body.target_bucket = targetBucket;
      const { data } = await api.post(`/dig/${encodeURIComponent(code)}/borrow`, body);
      toast.success(`Added to ${data.target_bucket || 'your collection'}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not borrow track');
    } finally {
      setBorrowingId(null);
    }
  }, [code, targetBucket]);

  // -------- End session --------
  const endSession = useCallback(async () => {
    if (!code) {
      onClose?.();
      return;
    }
    setBusy(true);
    try {
      await api.post(`/dig/${encodeURIComponent(code)}/close`);
    } catch (_) {
      // If already closed, that's fine.
    } finally {
      setBusy(false);
      onClose?.();
    }
  }, [code, onClose]);

  const qrValue = code
    ? `${typeof window !== 'undefined' ? window.location.origin : ''}/dig/${code}`
    : '';

  const timeLeft = status?.expires_in_seconds ?? 0;
  const timeLeftLabel = useMemo(() => {
    const s = Math.max(0, timeLeft);
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${String(r).padStart(2, '0')}`;
  }, [timeLeft]);

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) endSession();
      }}
    >
      <DialogContent
        className="max-w-2xl p-0 gap-0 overflow-hidden max-h-[92vh] flex flex-col"
        data-testid="dig-session-modal"
      >
        <DialogHeader className="px-4 pt-4 pb-3 border-b border-border">
          <div className="flex items-center justify-between gap-2">
            <DialogTitle className="flex items-center gap-2 text-base">
              <Shovel className="w-4 h-4 text-[hsl(var(--primary))]" />
              Dig Mode
              {status?.peer_username && step === 'active' && (
                <span className="text-xs text-muted-foreground font-normal">
                  with @{status.peer_username}
                </span>
              )}
            </DialogTitle>
            {(step === 'active' || step === 'host-waiting') && timeLeft > 0 && (
              <div className="flex items-center gap-1 text-[11px] font-mono text-muted-foreground">
                <Clock className="w-3 h-3" />
                <span className="tabular-nums">{timeLeftLabel}</span>
              </div>
            )}
          </div>
          <DialogDescription className="text-xs leading-relaxed">
            {step === 'chooser' && 'Browse a fellow GroovLenser\u2019s crates side by side. Tap tracks to borrow.'}
            {step === 'host-waiting' && 'Share the code below \u2014 the moment they join, you both dig into each other\u2019s crates.'}
            {step === 'guest-code' && 'Enter the 6-character code your GroovLenser gave you.'}
            {step === 'active' && 'Tap any crate to see the tracks inside. Tap a track to borrow it into your collection.'}
            {step === 'ended' && 'Session ended.'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto">
          {/* ------- CHOOSER ------- */}
          {step === 'chooser' && (
            <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={handleHost}
                disabled={busy}
                data-testid="dig-start-host"
                className="rounded-xl border border-border bg-card hover:border-[hsl(var(--primary))]/60 hover:bg-[hsl(var(--primary))]/5 p-4 text-left transition-colors"
              >
                <div className="flex items-center gap-2 mb-1">
                  <Users className="w-4 h-4 text-[hsl(var(--primary))]" />
                  <span className="text-sm font-medium">Host a Dig</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Generate a code + QR for a fellow GroovLenser to scan.
                </p>
              </button>
              <button
                type="button"
                onClick={() => setStep('guest-code')}
                data-testid="dig-join-code"
                className="rounded-xl border border-border bg-card hover:border-[hsl(var(--primary))]/60 hover:bg-[hsl(var(--primary))]/5 p-4 text-left transition-colors"
              >
                <div className="flex items-center gap-2 mb-1">
                  <Shovel className="w-4 h-4 text-[hsl(var(--primary))]" />
                  <span className="text-sm font-medium">Join by code</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Type the 6-character code your friend shows you.
                </p>
              </button>
            </div>
          )}

          {/* ------- HOST WAITING ------- */}
          {step === 'host-waiting' && code && (
            <div className="p-4 flex flex-col items-center gap-3">
              <div className="crate-neon-border rounded-2xl" style={{ padding: '4px' }}>
                <div className="bg-white p-4 rounded-[14px]">
                  <QRCodeSVG value={qrValue} size={220} level="M" />
                </div>
              </div>
              <div className="text-center">
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                  Or share this code
                </div>
                <div
                  className="text-3xl font-display font-semibold tracking-[0.3em] tabular-nums cursor-pointer select-all"
                  onClick={() => {
                    navigator.clipboard?.writeText(code);
                    toast.success('Code copied');
                  }}
                  data-testid="dig-host-code"
                  title="Tap to copy"
                >
                  {code}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-1 h-7 text-[11px]"
                  onClick={() => {
                    navigator.clipboard?.writeText(code);
                    toast.success('Code copied');
                  }}
                >
                  <CopyIcon className="w-3 h-3 mr-1" /> Copy code
                </Button>
              </div>
              <div className="text-xs text-muted-foreground flex items-center gap-2">
                <Loader2 className="w-3 h-3 animate-spin" />
                Waiting for the other GroovLenser to join…
              </div>
              <Button
                variant="outline"
                onClick={endSession}
                disabled={busy}
                className="rounded-lg w-full"
                data-testid="dig-cancel-host"
              >
                <XIcon className="w-3.5 h-3.5 mr-1.5" />
                Cancel
              </Button>
            </div>
          )}

          {/* ------- GUEST CODE ENTRY ------- */}
          {step === 'guest-code' && (
            <div className="p-4 space-y-3">
              <div>
                <Label htmlFor="dig-code-input" className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">
                  Dig code
                </Label>
                <Input
                  id="dig-code-input"
                  value={manualCode}
                  onChange={(e) => setManualCode(e.target.value.toUpperCase().slice(0, 12))}
                  placeholder="XXXXXX"
                  className="mt-1 h-11 text-center text-2xl font-display tracking-[0.3em] tabular-nums uppercase"
                  data-testid="dig-code-input"
                  autoFocus
                />
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => handleJoin()}
                  disabled={busy || manualCode.trim().length < 4}
                  className="rounded-lg flex-1"
                  data-testid="dig-join-submit"
                >
                  {busy ? (
                    <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <Shovel className="w-3.5 h-3.5 mr-1.5" />
                  )}
                  Join Dig
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setStep('chooser')}
                  disabled={busy}
                  className="rounded-lg"
                >
                  <XIcon className="w-3.5 h-3.5 mr-1.5" />
                  Back
                </Button>
              </div>
            </div>
          )}

          {/* ------- ACTIVE SESSION ------- */}
          {step === 'active' && (
            <div className="p-4 space-y-3">
              {/* Target-crate picker (persists across borrows during session) */}
              <div className="rounded-lg border border-border p-2.5 flex items-center gap-2">
                <Label
                  htmlFor="dig-target-bucket"
                  className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono shrink-0"
                >
                  Borrow into
                </Label>
                <select
                  id="dig-target-bucket"
                  value={targetBucket}
                  onChange={(e) => setTargetBucket(e.target.value)}
                  className="flex-1 h-8 rounded-lg border border-border bg-background text-xs px-2"
                  data-testid="dig-target-bucket"
                >
                  <option value="">Original bucket (from peer)</option>
                  {myBuckets.map((b) => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
              </div>

              {/* Peer crates grid OR track list */}
              {!selectedBucket ? (
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">
                    @{status?.peer_username || 'peer'}&rsquo;s crates
                  </div>
                  {peerAlbums.length === 0 ? (
                    <div className="text-center text-xs text-muted-foreground py-8">
                      This GroovLenser doesn&rsquo;t have any crates yet.
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                      {peerAlbums.map((a) => (
                        <div
                          key={a.genre_bucket}
                          onClick={() => openBucket(a.genre_bucket)}
                          className="cursor-pointer"
                          data-testid={`dig-peer-crate-${a.genre_bucket}`}
                        >
                          <div className="pointer-events-none">
                            <GenreAlbumTile
                              bucket={a.genre_bucket}
                              count={a.count}
                              art={a.art || []}
                              coverPath={a.cover_path || null}
                              coverType={a.cover_type || 'default_collage'}
                              coverConfig={a.cover_config || {}}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedBucket(null);
                        setPeerCards([]);
                      }}
                      className="h-7 text-xs"
                      data-testid="dig-back-to-crates"
                    >
                      <ChevronLeft className="w-3.5 h-3.5 mr-1" />
                      Crates
                    </Button>
                    <div className="text-sm font-medium flex items-center gap-1.5">
                      <Package className="w-3.5 h-3.5 text-[hsl(var(--primary))]" />
                      {selectedBucket}
                    </div>
                    <div className="text-[11px] text-muted-foreground ml-auto">
                      {peerCards.length} tracks
                    </div>
                  </div>
                  {loadingPeerCards ? (
                    <div className="text-center text-xs text-muted-foreground flex items-center justify-center gap-2 py-8">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      Loading tracks…
                    </div>
                  ) : (
                    <ScrollArea className="max-h-[50vh] rounded-lg border border-border">
                      <div className="p-1.5 space-y-1">
                        {peerCards.map((c) => {
                          const isBusy = borrowingId === c.id;
                          return (
                            <div
                              key={c.id}
                              className="flex items-center gap-2 p-2 rounded-md hover:bg-secondary/50"
                              data-testid={`dig-peer-card-${c.id}`}
                            >
                              {c.album_art_url && (
                                <img
                                  src={c.album_art_url}
                                  alt=""
                                  className="w-10 h-10 rounded object-cover shrink-0"
                                />
                              )}
                              <div className="min-w-0 flex-1">
                                <div className="text-xs font-medium truncate">
                                  {c.title || 'Untitled'}
                                </div>
                                <div className="text-[10px] text-muted-foreground truncate">
                                  {c.artist || ''}
                                  {c.bpm_key && ` · ${c.bpm_key}`}
                                </div>
                              </div>
                              <Button
                                size="sm"
                                onClick={() => borrowCard(c)}
                                disabled={isBusy}
                                className="h-7 rounded-lg text-[11px]"
                                data-testid={`dig-borrow-${c.id}`}
                              >
                                {isBusy ? (
                                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                ) : (
                                  <Check className="w-3 h-3 mr-1" />
                                )}
                                Borrow
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    </ScrollArea>
                  )}
                </div>
              )}

              <Button
                variant="outline"
                onClick={endSession}
                disabled={busy}
                className="w-full rounded-lg mt-2"
                data-testid="dig-end-session"
              >
                <XIcon className="w-3.5 h-3.5 mr-1.5" />
                End Dig session
              </Button>
            </div>
          )}

          {/* ------- ENDED ------- */}
          {step === 'ended' && (
            <div className="p-6 text-center space-y-3">
              <div className="mx-auto w-12 h-12 rounded-full bg-[hsl(var(--primary))]/10 grid place-items-center">
                <Music className="w-5 h-5 text-[hsl(var(--primary))]" />
              </div>
              <div className="text-sm font-medium">Dig session ended</div>
              <div className="text-xs text-muted-foreground">
                Any tracks you borrowed are now in your collection.
              </div>
              <Button onClick={onClose} className="rounded-lg" data-testid="dig-done-button">
                Done
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
